import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth.jsx';
import { apiClient, parseApiError } from '../../services/http.js';
import { useModalA11y } from '../../hooks/useModalA11y.js';

export default function TermsAcceptanceModal() {
  const { refreshSession, logout } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleConfirm = async () => {
    if (!accepted) return;
    setLoading(true);
    setError(null);

    try {
      const { res, data } = await apiClient.acceptTerms({
        terms_version: '2026-05-20',
        policy_version: '2026-05-20',
      });

      if (!res.ok) {
        throw new Error(parseApiError(data, 'Erro ao confirmar o aceite. Tente novamente.'));
      }

      await refreshSession();
    } catch (err) {
      setError(err.message || 'Falha de rede ao se conectar com o servidor.');
    } finally {
      setLoading(false);
    }
  };

  const modalA11y = useModalA11y(true, null, 'terms-title');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
      <div {...modalA11y} className="bg-surface-container-high border border-outline-variant/30 max-w-md w-full rounded-2xl p-6 md:p-8 shadow-2xl flex flex-col space-y-6 animate-scale-up text-on-surface">
        {/* Header Icon */}
        <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2 text-primary shadow-inner">
          <span className="material-symbols-outlined text-3xl" style={{ fontVariationSettings: "'FILL' 1" }}>
            gavel
          </span>
        </div>

        {/* Title & Desc */}
        <div className="text-center space-y-2">
          <h2 id="terms-title" className="font-serif text-2xl font-bold tracking-tight leading-snug">
            Atualização de Termos e Privacidade
          </h2>
          <p className="text-sm font-light text-on-surface-variant leading-relaxed">
            Para continuar utilizando o CatholicGPT e garantir a adequação à LGPD (Lei Geral de Proteção de Dados), revise e aceite os novos documentos legais aplicáveis.
          </p>
        </div>

        {/* Quick Document Links */}
        <div className="grid grid-cols-1 gap-3">
          <a
            href="/termos"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-3.5 rounded-xl border border-outline-variant/50 bg-surface-container-low hover:bg-primary/5 hover:border-primary/40 transition-all group"
          >
            <span className="material-symbols-outlined text-primary text-xl group-hover:scale-110 transition-transform">
              description
            </span>
            <div className="flex-1 text-left">
              <div className="text-sm font-semibold">Termos de Uso</div>
              <div className="text-xs text-on-surface-variant opacity-80">Versão 1.0 — 20 de Maio de 2026</div>
            </div>
            <span className="material-symbols-outlined text-xs text-on-surface-variant">
              open_in_new
            </span>
          </a>

          <a
            href="/privacidade"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-3.5 rounded-xl border border-outline-variant/50 bg-surface-container-low hover:bg-primary/5 hover:border-primary/40 transition-all group"
          >
            <span className="material-symbols-outlined text-primary text-xl group-hover:scale-110 transition-transform">
              security
            </span>
            <div className="flex-1 text-left">
              <div className="text-sm font-semibold">Política de Privacidade</div>
              <div className="text-xs text-on-surface-variant opacity-80">Versão 1.0 — 20 de Maio de 2026</div>
            </div>
            <span className="material-symbols-outlined text-xs text-on-surface-variant">
              open_in_new
            </span>
          </a>
        </div>

        {/* Checkbox Section */}
        <label className="flex items-start gap-3 p-3.5 rounded-xl bg-surface-container-lowest border border-outline-variant/20 cursor-pointer select-none hover:bg-surface-container-low transition-colors">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            disabled={loading}
            className="mt-0.5 h-5 w-5 rounded border-outline-variant text-primary focus:ring-primary accent-primary cursor-pointer disabled:opacity-50"
          />
          <span className="text-xs font-medium leading-relaxed">
            Li e aceito expressamente os novos Termos de Uso e a Política de Privacidade do CatholicGPT.
          </span>
        </label>

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-error/10 border border-error/20 text-error rounded-xl text-xs flex items-start gap-2 animate-shake">
            <span className="material-symbols-outlined text-sm mt-0.5">error</span>
            <span className="font-medium">{error}</span>
          </div>
        )}

        {/* Confirm Button */}
        <button
          onClick={handleConfirm}
          disabled={!accepted || loading}
          className="w-full py-3.5 bg-primary text-on-primary font-bold rounded-xl shadow-lg hover:opacity-95 active:scale-[0.98] disabled:opacity-40 disabled:scale-100 disabled:pointer-events-none transition-all flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <svg
                className="animate-spin h-5 w-5 text-on-primary"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span>Confirmando...</span>
            </>
          ) : (
            <>
              <span className="material-symbols-outlined text-xl">done_all</span>
              <span>Confirmar Aceite</span>
            </>
          )}
        </button>

        {/* Logout Option */}
        <button
          onClick={logout}
          disabled={loading}
          className="text-xs text-on-surface-variant hover:text-primary transition-colors font-medium underline block mx-auto pt-2 disabled:opacity-50"
        >
          Sair da conta
        </button>
      </div>
    </div>
  );
}
